/* eslint-disable max-lines */
/* eslint-disable no-restricted-imports */
// FSD VIOLATION: This layout component orchestrates composition display using entities,
// features, and widgets. Moving to widgets would break features that import it.
import { useMemo, useRef, useState, useEffect } from "react";
import { Badge, Button, ResellerPricingIcon } from "@design-lab/shared/ui";
import { useDesignLabConfig, useIsSample, usePrintReseller } from "@design-lab/shared/lib/config";
import {
  useCompositionsActions,
  useCompositionsList,
  useCurrentComposition
} from "@design-lab/entities/composition";
import { ChangeShirt } from "@design-lab/widgets/artboard/ui/tool-bar/ChangeShirt";
import { AddColorModal } from "@design-lab/features/select-product-color/ui/AddColorModal";
import { useProductColors } from "@design-lab/entities/product";
import { RefreshCcw, Palette, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { buildQuantitiesForSummary } from "@design-lab/entities/composition/lib/design-project/composition";
import { useCompositionPricing } from "@design-lab/entities/composition/model/useCompositionPricing";
import { PriceDisplay } from "@design-lab/features/manage-product-sizing/ui/PriceDisplay";
import type { printOnDemand } from "common/utils/models";
import { BLANKS_SIZES } from "@design-lab/shared/constants";
import { calculateBasePrice } from "@design-lab/features/manage-product-sizing/ui/calculateBasePrice";
import { usePriceExposedTracking } from "@design-lab/shared/hooks/usePriceExposedTracking";

const ResellerPricingButton = () => {
  const printReseller = usePrintReseller();

  useEffect(() => {
    if (printReseller) {
      import("components/jiffy/masthead-redesign2025/reseller-pricing-modal").then(module => {
        module.default();
      });
    }
  }, [printReseller]);

  if (!printReseller) return null;

  return (
    <button
      type="button"
      className="js-reseller-pricing-button-dl flex cursor-pointer items-center gap-1.5 text-xs font-medium text-green-700 underline"
      data-testid="masthead-account-dropdown-reseller-pricing-button"
    >
      <ResellerPricingIcon />
      <span>Wholesale Pricing</span>
    </button>
  );
};

export function CompositionInfo() {
  const [isAddColorModalOpen, setIsAddColorModalOpen] = useState(false);

  const { isDesktop } = useResponsive();
  const { colors, selectedColor, addedCompositionIds, maxVisibleColors, recommendedColors } =
    useProductColors({
      maxColors: 24
    });
  const { currentProduct, currentComposition } = useCurrentComposition();
  const { addComposition, selectCurrentComposition, removeComposition } = useCompositionsActions();
  const { getCompositionPrice, getVariantPrice } = useCompositionPricing();
  const { formCompositions } = useCompositionsList();
  const { isProductsTab, isSizeTab, isInfoExpanded, toggleInfoExpanded, setTab } = useTab();
  const isSample = useIsSample();
  const config = useDesignLabConfig();
  const madeSamplesV1 = config.flip?.madeSamplesV1;
  const isSampleV0 = isSample && !madeSamplesV1;
  const isSampleV1 = isSample && madeSamplesV1;

  const handleAddColor = () => {
    setIsAddColorModalOpen(true);
  };

  const handleModifySizes = () => {
    setTab("size");
  };

  const handleColorSelect = (colorId: string) => {
    const color = [...recommendedColors, ...colors].find(c => c.id === colorId);
    if (!color || !currentProduct) return;

    const newComposition = currentProduct.compositions.find(comp => comp.colorName === color.name);
    if (newComposition && !addedCompositionIds.has(newComposition.id)) {
      addComposition({
        catalogId: currentProduct.catalogNumber,
        colorName: newComposition.colorName,
        compositionId: newComposition.id
      });
      selectCurrentComposition(newComposition.id);
      setIsAddColorModalOpen(false);
    }
  };

  const handleRemoveComposition = () => {
    if (currentComposition && formCompositions.length > 1) {
      removeComposition(currentComposition.id);
    }
  };

  const variantPrice = (variantId: string) =>
    currentComposition && getVariantPrice(currentComposition.id, variantId);

  const basePrice = currentComposition && calculateBasePrice(currentComposition, variantPrice);

  const basePriceSizes: Array<{
    start: printOnDemand.getProduct.Size;
    end: printOnDemand.getProduct.Size;
  }> = useMemo(() => {
    if (!basePrice?.sizes || basePrice.sizes.length === 0) {
      return [];
    }

    // Sort sizes according to BLANKS_SIZES order
    const sortedSizes = [...basePrice.sizes].sort((a, b) => {
      const indexA = BLANKS_SIZES.indexOf(a);
      const indexB = BLANKS_SIZES.indexOf(b);
      return indexA - indexB;
    });

    const intervals: Array<{
      start: printOnDemand.getProduct.Size;
      end: printOnDemand.getProduct.Size;
    }> = [];

    if (sortedSizes.length === 0) {
      return intervals;
    }

    const firstSize = sortedSizes[0];
    if (!firstSize) {
      return intervals;
    }

    let currentStart = firstSize;
    let currentEnd = firstSize;

    for (let i = 1; i < sortedSizes.length; i += 1) {
      const currentSize = sortedSizes[i];
      if (currentSize) {
        const currentIndex = BLANKS_SIZES.indexOf(currentSize);
        const previousIndex = BLANKS_SIZES.indexOf(currentEnd);

        // Check if current size is consecutive to the previous one
        if (currentIndex !== -1 && previousIndex !== -1 && currentIndex === previousIndex + 1) {
          // Extend the current interval
          currentEnd = currentSize;
        } else {
          // Start a new interval
          intervals.push({ start: currentStart, end: currentEnd });
          currentStart = currentSize;
          currentEnd = currentSize;
        }
      }
    }

    // Add the last interval
    intervals.push({ start: currentStart, end: currentEnd });

    return intervals;
  }, [basePrice]);

  const subTotal = useMemo(
    () => (currentComposition ? getCompositionPrice(currentComposition.id) : 0),
    [currentComposition, getCompositionPrice]
  );

  const summaryQuantities = useMemo(
    () =>
      currentComposition ? buildQuantitiesForSummary(currentComposition, formCompositions) : {},
    [currentComposition, formCompositions]
  );

  const totalQuantity = useMemo(
    () => Object.values(summaryQuantities).reduce((sum, qty) => sum + (qty as number), 0),
    [summaryQuantities]
  );

  const compositionRef = useRef<HTMLDivElement>(null);
  // Fires when user sees price-per-shirt (PriceDisplay), even before adding quantities
  usePriceExposedTracking(
    compositionRef,
    {
      component: "CompositionInfo/PriceDisplay",
      step: "DesignLab",
      priceShown: basePrice?.value || 0,
      catalogNumber: currentProduct?.catalogNumber,
      quantity: totalQuantity > 0 ? totalQuantity : 1,
      businessLine: "print"
    },
    isInfoExpanded && (basePrice?.value || 0) > 0
  );

  // Fires when user sees the actual sub-total (after adding quantities)
  usePriceExposedTracking(
    compositionRef,
    {
      component: "CompositionInfo/SubTotal",
      step: "DesignLab",
      priceShown: subTotal,
      catalogNumber: currentProduct?.catalogNumber,
      quantity: totalQuantity,
      businessLine: "print"
    },
    isInfoExpanded && subTotal > 0 && totalQuantity > 0
  );

  const productFullName =
    `${currentProduct?.brand || ""} ${currentProduct?.catalogNumber || ""} - ${currentProduct?.name || ""}`.trim();

  if (!currentComposition || !currentProduct) return null;

  return (
    <div ref={compositionRef} className="order-0 flex flex-col gap-1 rounded bg-gray-100 p-2">
      <div className="flex items-center justify-between gap-1" data-testid="product-info-container">
        <div className="flex items-center gap-1 text-sm" data-testid="product-info-title">
          {isSampleV1 && <Badge size="sm">Sample</Badge>}
          {productFullName && <p className="font-semibold">{productFullName}</p>}
        </div>

        <Button
          data-testid="expand-composition-button"
          variant="outline"
          size="icon"
          onPress={toggleInfoExpanded}
          aria-label="Expand composition"
          className="min-w-8"
        >
          {isInfoExpanded ? <ChevronUp /> : <ChevronDown />}
        </Button>
      </div>

      {isInfoExpanded && (
        <>
          <div className="flex flex-col gap-1 lg:flex-row lg:flex-wrap">
            <p className="flex items-center gap-1 text-xs" data-testid="color-info-container">
              <span className="text-gray-500">Color:</span>{" "}
              <span className="font-semibold" data-testid="color-info-name">
                {currentComposition.colorName}
              </span>
              {currentComposition.materials[0] && (
                <Badge size="sm" className="text-primary rounded bg-gray-300 capitalize">
                  {currentComposition.materials[0]}
                </Badge>
              )}
            </p>

            {Object.keys(summaryQuantities).length > 0 && (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-500">Sizes:</span>{" "}
                <div data-testid="sizes-summary-value" className="flex flex-wrap gap-1">
                  {Object.entries(summaryQuantities).map(([size, quantity]) => (
                    <span key={size} className="font-medium">
                      {quantity} ({size})
                    </span>
                  ))}
                </div>
                {!isSizeTab && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-fit px-1 text-xs font-medium text-blue-700 underline"
                    data-testid="modify-sizes-button"
                    onPress={handleModifySizes}
                  >
                    Modify Sizes
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 pt-1 lg:flex-row lg:gap-2 lg:pt-0">
            <div className="flex items-center gap-2 lg:flex-col lg:items-start lg:gap-1">
              {basePrice && basePriceSizes.length > 0 ? (
                <PriceDisplay
                  basePriceSizes={basePriceSizes}
                  basePrice={basePrice}
                  isSample={isSample}
                  selectedSizes={summaryQuantities}
                  getVariantPrice={variantPrice}
                />
              ) : (
                <p className="mb-1 flex items-center gap-1.5 text-xs lg:mb-0.5">
                  <span className="h-4 w-16 animate-pulse rounded bg-gray-200" />
                  <span className="h-4 w-12 animate-pulse rounded bg-gray-200" />
                </p>
              )}

              <div className="flex items-center gap-2">
                <p className="text-xs">
                  <span className="text-gray-500">Sub Total:</span>{" "}
                  <span className="font-bold">${subTotal.toFixed(2)}</span>
                </p>
                {isDesktop && <ResellerPricingButton />}
              </div>
            </div>
            {!isDesktop && <ResellerPricingButton />}
          </div>
        </>
      )}

      {isProductsTab && !isSampleV0 && (
        <div className="mt-2 flex gap-2">
          {!isSample && (
            <Button
              data-testid="add-another-color-button"
              size={isDesktop ? "lg" : "sm"}
              variant="outline"
              className="w-auto gap-1 lg:gap-2"
              onPress={handleAddColor}
            >
              <Palette width={16} height={16} className="!h-4 !w-4 lg:mr-1" />
              Add Another Color
            </Button>
          )}

          <ChangeShirt
            icon={<RefreshCcw width={16} height={16} className="!h-4 !w-4 lg:mr-1" />}
            size={isDesktop ? "lg" : "sm"}
            className="w-auto gap-1 lg:gap-2"
          />

          {formCompositions.length > 1 && (
            <Button
              data-testid="remove-composition-button"
              variant="outline"
              size={isDesktop ? "icon_lg" : "icon"}
              onPress={handleRemoveComposition}
              aria-label="Remove composition"
              className={isDesktop ? "min-w-10" : "min-w-8"}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      )}

      <AddColorModal
        isOpen={isAddColorModalOpen}
        onClose={() => setIsAddColorModalOpen(false)}
        recommendedColors={recommendedColors}
        otherColors={colors}
        currentProduct={currentProduct}
        selectedColor={selectedColor}
        onColorSelect={handleColorSelect}
        maxColors={maxVisibleColors}
      />
    </div>
  );
}
